import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Ban,
  CalendarClock,
  Mail,
  RotateCcw,
  Send,
  UserRoundCog,
} from 'lucide-react';
import { LocalizedName } from '@/components/localized-name';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePermission } from '@/features/auth/auth.store';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { VoucherActionDialog, type VoucherAction } from './voucher-action-dialog';
import { restrictionLines, voucherState } from './voucher-format';
import { useVoucherDetail } from './vouchers.api';
import {
  CHANGE_KIND,
  CLAIM_METHOD,
  VOUCHER_STATUS,
  type AdminVoucherDetail,
  type VoucherPayment,
  type VoucherPerson,
} from './types';

/**
 * Everything about one voucher from a single `GET v2/admin/vouchers/{id}`,
 * and every action on it.
 *
 * Actions are offered only where the API would accept them: nothing but
 * viewing on a voided or refunded voucher, and only void on an unpaid one;
 * change-email only while unclaimed; void and refund only for the roles the
 * API allows.
 */

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-0.5 border-b py-2.5 last:border-0 sm:grid-cols-[10rem_1fr] sm:gap-4">
      <p className="text-muted-foreground text-xs font-semibold">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function Person({ person }: { person: VoucherPerson | null }) {
  if (!person) return <span className="text-muted-foreground">—</span>;
  return (
    <>
      <p>{person.name || '—'}</p>
      {person.email && (
        <p className="text-muted-foreground text-xs">{person.email}</p>
      )}
    </>
  );
}

function EmptyList({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
      {children}
    </p>
  );
}

const CLAIM_METHOD_KEY: Record<number, string> = {
  [CLAIM_METHOD.self]: 'vouchers.claimMethod.self',
  [CLAIM_METHOD.autoByEmail]: 'vouchers.claimMethod.autoByEmail',
  [CLAIM_METHOD.code]: 'vouchers.claimMethod.code',
  [CLAIM_METHOD.admin]: 'vouchers.claimMethod.admin',
};

function PaymentBadge({ payment }: { payment: VoucherPayment }) {
  const { t } = useTranslation();
  if (payment.voidedAt) {
    return <Badge variant="secondary">{t('vouchers.payment.voided')}</Badge>;
  }
  if (payment.refundedAmount > 0) {
    return (
      <Badge variant="outline">
        {t('vouchers.payment.refunded', {
          amount: formatCurrency(payment.refundedAmount),
        })}
      </Badge>
    );
  }
  if (payment.status === 1) {
    return <Badge variant="success">{t('vouchers.payment.success')}</Badge>;
  }
  return payment.status === 2 ? (
    <Badge variant="warning">{t('vouchers.payment.pending')}</Badge>
  ) : (
    <Badge variant="destructive">{t('vouchers.payment.failed')}</Badge>
  );
}

export function VoucherDetailSheet({
  voucherId,
  open,
  onOpenChange,
}: {
  voucherId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const canRefund = usePermission('canRefundVouchers');
  const detail = useVoucherDetail(open ? voucherId : null);
  const voucher = detail.data ?? null;
  const [action, setAction] = useState<VoucherAction | null>(null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-3xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {voucher ? (
              <span className="font-mono tracking-wider">{voucher.code}</span>
            ) : (
              t('vouchers.voucher')
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="p-4">
          {detail.isPending && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-8 w-28" />
                ))}
              </div>
              <Skeleton className="h-9 w-80 max-w-full" />
              <div className="space-y-3 rounded-xl border p-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="flex justify-between gap-3">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail.isError && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('vouchers.detailFailed')}
            </p>
          )}

          {voucher && (
            // After an action the detail refetches; the old answer stays
            // readable under the spinner until the new one lands.
            <div className="relative">
              <BusyOverlay show={detail.isFetching && !detail.isPending} />
              <div
                className={cn(
                  'space-y-4',
                  detail.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                <Actions
                  voucher={voucher}
                  canRefund={canRefund}
                  onAction={setAction}
                />

                <Tabs defaultValue="summary">
                  <TabsList>
                    <TabsTrigger value="summary">
                      {t('vouchers.summary')}
                    </TabsTrigger>
                    <TabsTrigger value="bookings">
                      {t('vouchers.bookings')} ({voucher.redemptions.length})
                    </TabsTrigger>
                    <TabsTrigger value="payments">
                      {t('vouchers.payments')} ({voucher.payments.length})
                    </TabsTrigger>
                    <TabsTrigger value="history">
                      {t('vouchers.history')} ({voucher.history.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="summary">
                    <Summary voucher={voucher} language={language} />
                  </TabsContent>

                  <TabsContent value="bookings">
                    {voucher.redemptions.length === 0 ? (
                      <EmptyList>{t('vouchers.noBookings')}</EmptyList>
                    ) : (
                      <div className="divide-y rounded-xl border">
                        {voucher.redemptions.map((r) => (
                          <div
                            key={r.id}
                            className="flex items-start justify-between gap-3 p-3 text-sm"
                          >
                            <div className="min-w-0">
                              <LocalizedName
                                nameEn={r.eventNameEn}
                                nameEs={r.eventNameEs}
                              />
                              <p className="text-muted-foreground text-xs">
                                {formatDateTime(r.startTime)} ·{' '}
                                {r.bookedFor?.name ?? '—'}
                              </p>
                              {r.redeemedBy && (
                                <p className="text-muted-foreground text-xs">
                                  {t('vouchers.bookedByStaff', {
                                    name: r.redeemedBy.name,
                                  })}
                                </p>
                              )}
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="tabular-nums">
                                {formatCurrency(r.value)}
                              </p>
                              {r.returnedAt ? (
                                <Badge variant="secondary">
                                  {t('vouchers.useReturned')}
                                </Badge>
                              ) : (
                                <Badge variant="success">
                                  {t('vouchers.useSpent')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="payments">
                    {voucher.payments.length === 0 ? (
                      <EmptyList>
                        {voucher.isComplimentary
                          ? t('vouchers.complimentaryNoPayment')
                          : t('vouchers.noPayments')}
                      </EmptyList>
                    ) : (
                      <div className="divide-y rounded-xl border">
                        {voucher.payments.map((p) => (
                          <div
                            key={p.id}
                            className="flex items-start justify-between gap-3 p-3 text-sm"
                          >
                            <div className="min-w-0">
                              <p className="font-medium">
                                {t(paymentMethodLabelKey(p.method))}
                              </p>
                              <p className="text-muted-foreground text-xs">
                                {formatDateTime(p.createdAt)}
                                {p.offlineReference && ` · ${p.offlineReference}`}
                                {p.paypalReferenceNumber &&
                                  ` · ${p.paypalReferenceNumber}`}
                              </p>
                            </div>
                            <div className="shrink-0 space-y-1 text-right">
                              <p className="font-medium tabular-nums">
                                {formatCurrency(p.amount)}
                              </p>
                              <PaymentBadge payment={p} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="history">
                    {voucher.history.length === 0 ? (
                      <EmptyList>{t('vouchers.noHistory')}</EmptyList>
                    ) : (
                      <ol className="divide-y rounded-xl border">
                        {voucher.history.map((change, index) => (
                          <li key={index} className="space-y-0.5 p-3 text-sm">
                            <p className="font-medium">
                              {change.kind === CHANGE_KIND.emailChanged
                                ? t('vouchers.change.emailChanged', {
                                    from: change.fromEmail ?? '—',
                                    to: change.toEmail ?? '—',
                                  })
                                : change.kind === CHANGE_KIND.reassigned
                                  ? t('vouchers.change.reassigned', {
                                      from:
                                        change.from?.name ??
                                        change.fromEmail ??
                                        t('vouchers.nobody'),
                                      to: change.to?.name ?? '—',
                                    })
                                  : t('vouchers.change.claimed', {
                                      name: change.to?.name ?? '—',
                                      how: t(
                                        CLAIM_METHOD_KEY[change.claimMethod ?? -1] ??
                                          'vouchers.claimMethod.admin'
                                      ),
                                    })}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {formatDateTime(change.changedAt)}
                              {change.changedBy && ` · ${change.changedBy.name}`}
                            </p>
                            {change.reason && (
                              <p className="text-muted-foreground text-xs italic">
                                {change.reason}
                              </p>
                            )}
                          </li>
                        ))}
                      </ol>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          )}
        </div>

        {voucher && action && (
          <VoucherActionDialog
            key={action}
            voucher={voucher}
            action={action}
            onClose={() => setAction(null)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function Actions({
  voucher,
  canRefund,
  onAction,
}: {
  voucher: AdminVoucherDetail;
  canRefund: boolean;
  onAction: (action: VoucherAction) => void;
}) {
  const { t } = useTranslation();
  const active = voucher.status === VOUCHER_STATUS.active;
  const unclaimed = !voucher.holder;
  const refundable = voucher.amountPaid - voucher.amountRefunded > 0;

  const buttons: {
    action: VoucherAction;
    icon: typeof Send;
    show: boolean;
    destructive?: boolean;
  }[] = [
    {
      action: 'resend',
      icon: Send,
      // Nowhere to send a code-only voucher nobody has claimed.
      show: active && (!unclaimed || Boolean(voucher.recipientEmail)),
    },
    { action: 'email', icon: Mail, show: active && unclaimed },
    { action: 'reassign', icon: UserRoundCog, show: active },
    { action: 'extend', icon: CalendarClock, show: active },
    {
      action: 'void',
      icon: Ban,
      // The API also voids unpaid ones: an abandoned checkout, or a stuck issue.
      show: (active || voucher.status === VOUCHER_STATUS.pendingPayment) && canRefund,
      destructive: true,
    },
    {
      action: 'refund',
      icon: RotateCcw,
      show: active && canRefund && refundable,
      destructive: true,
    },
  ];

  const visible = buttons.filter((b) => b.show);
  if (visible.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {visible.map(({ action, icon: Icon, destructive }) => (
        <Button
          key={action}
          size="sm"
          variant="outline"
          className={destructive ? 'text-destructive' : undefined}
          onClick={() => onAction(action)}
        >
          <Icon />
          {t(`vouchers.actionDialog.${action}.button`)}
        </Button>
      ))}
    </div>
  );
}

function Summary({
  voucher,
  language,
}: {
  voucher: AdminVoucherDetail;
  language: string;
}) {
  const { t } = useTranslation();
  const state = voucherState(voucher);

  return (
    <div className="rounded-xl border px-4">
      <Row label={t('vouchers.status')}>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={state.tone}>{t(state.key)}</Badge>
          {voucher.isComplimentary && (
            <Badge variant="outline">{t('vouchers.complimentary')}</Badge>
          )}
          {voucher.isCustom && (
            <Badge variant="outline">{t('vouchers.custom')}</Badge>
          )}
        </div>
        {voucher.statusReason && (
          <p className="text-muted-foreground mt-1 text-xs">
            {voucher.statusReason}
          </p>
        )}
      </Row>

      <Row label={t('vouchers.code')}>
        <span className="inline-flex items-center gap-1 font-mono tracking-wider">
          {voucher.code}
          <CopyButton value={voucher.code} />
        </span>
      </Row>

      <Row label={t('vouchers.name')}>
        <LocalizedName nameEn={voucher.nameEn} nameEs={voucher.nameEs} />
      </Row>

      <Row label={t('vouchers.uses')}>
        {t('vouchers.usesOf', {
          used: voucher.usesUsed,
          total: voucher.usesTotal,
          remaining: voucher.usesRemaining,
        })}
      </Row>

      <Row label={t('vouchers.expires')}>
        {voucher.expiresAt ? formatDate(voucher.expiresAt) : t('vouchers.never')}
        {voucher.isExpired && (
          <Badge variant="warning" className="ml-2">
            {t('vouchers.state.expired')}
          </Badge>
        )}
      </Row>

      <Row label={t('vouchers.holder')}>
        {voucher.holder ? (
          <>
            <Person person={voucher.holder} />
            {voucher.claimedAt && (
              <p className="text-muted-foreground text-xs">
                {t('vouchers.claimedOn', {
                  date: formatDateTime(voucher.claimedAt),
                })}
              </p>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">
            {t('vouchers.state.unclaimed')}
          </span>
        )}
      </Row>

      <Row label={t('vouchers.sentTo')}>
        {voucher.recipientEmail ? (
          <>
            <p>{voucher.recipientName || voucher.recipientEmail}</p>
            {voucher.recipientName && (
              <p className="text-muted-foreground text-xs">
                {voucher.recipientEmail}
              </p>
            )}
            <p className="text-muted-foreground text-xs">
              {t('vouchers.emailIn', {
                language: voucher.recipientLanguage === 1 ? 'Español' : 'English',
              })}
            </p>
          </>
        ) : (
          <span className="text-muted-foreground">{t('vouchers.codeOnly')}</span>
        )}
      </Row>

      <Row label={t('vouchers.buyer')}>
        {voucher.buyer ? (
          <Person person={voucher.buyer} />
        ) : (
          <span className="text-muted-foreground">
            {t('vouchers.issuedByStaff', {
              name: voucher.issuedBy?.name ?? '—',
            })}
          </span>
        )}
      </Row>

      <Row label={t('vouchers.paid')}>
        <p className="tabular-nums">{formatCurrency(voucher.amountPaid)}</p>
        {voucher.amountRefunded > 0 && (
          <p className="text-muted-foreground text-xs">
            {t('vouchers.refundedAmount', {
              amount: formatCurrency(voucher.amountRefunded),
            })}
          </p>
        )}
      </Row>

      {voucher.message && (
        <Row label={t('vouchers.message')}>
          <p className="whitespace-pre-line italic">{voucher.message}</p>
        </Row>
      )}

      <Row label={t('vouchers.restrictions')}>
        {restrictionLines(voucher.restrictions, language, t).map((line) => (
          <p key={line}>{line}</p>
        ))}
      </Row>

      <Row label={t('vouchers.created')}>{formatDateTime(voucher.createdAt)}</Row>

      {voucher.adminNotes && (
        <Row label={t('vouchers.adminNotes')}>
          <p className="whitespace-pre-line">{voucher.adminNotes}</p>
        </Row>
      )}
    </div>
  );
}
